import type { componentsMapType, MovementCandidate, PlacementStrategy } from "@/src/shared/contracts/app";
import {
  clampPositionToBounds,
  movementCost,
  occupancyRect,
  rectsOverlap,
  type LayoutPoint as Point,
  type LayoutRect as Rect,
  type LayoutSize as Size,
} from "@/src/state/appStore/layoutGeometry";

type LayoutPlannerOptions = {
  components: componentsMapType[];
  screen: Size;
  newComponentSize: Size;
  desiredCenter: Point;
  margin?: number;
  maxMovements?: number;
  gridStep?: number;
  /**
   * Visible windows the planner must route around but never relocate
   * (transient prompts such as approvals and job status). They occupy space
   * exactly like any other visible window.
   */
  fixedComponentNames?: Set<string>;
  maxTargetCandidates?: number;
  /**
   * The area a window covers when it sits at `position`. Defaults to its
   * size. Windows that grow on their own (windowFootprint.ts) cover more, and
   * how much can depend on where they sit, so a moved window is checked with
   * the footprint it has at its destination. Never smaller than the size:
   * relocation skips positions where the window alone already overlaps.
   */
  footprintAt?: FootprintAt;
  /**
   * Screen areas no window may take, such as app chrome (workspaceChrome.ts).
   * Unlike windows they carry no margin of their own: a window keeps only its
   * own margin from them. The new window and every relocated window go around
   * them.
   */
  reservedRects?: Rect[];
  stats?: LayoutPlannerStats;
};

type FootprintAt = (component: componentsMapType, position: Point) => Size;

const ownSize: FootprintAt = (component) => component.size;

const DEFAULT_MARGIN = 3;
const DEFAULT_GRID_STEP = 24;
const DEFAULT_MAX_MOVEMENTS = 5;
const DEFAULT_MAX_TARGET_CANDIDATES = 320;
const TARGET_DISTANCE_COST = 4;
const MOVEMENT_COUNT_COST = 160;

function rectWithinScreen(rect: Rect, screen: Size): boolean {
  return (
    rect.x >= 0 &&
    rect.y >= 0 &&
    rect.x + rect.width <= screen.width &&
    rect.y + rect.height <= screen.height
  );
}

function componentRect(component: componentsMapType, margin: number, footprintAt: FootprintAt): Rect {
  return occupancyRect(component.position, footprintAt(component, component.position), margin);
}

// Minimized windows live in the dock and leave their workspace area free.
function isHiddenComponent(component: componentsMapType): boolean {
  return Boolean(component.isMinimized);
}

// Fixed windows still occupy space; they are obstacles the plan cannot move.
// Dependency windows follow their parent, so the planner never moves them alone.
function isFixedComponent(component: componentsMapType, fixedNames: Set<string>): boolean {
  return Boolean(fixedNames.has(component.name) || component.parentComponentId);
}

function overlapsAny(rect: Rect, rects: Rect[]): boolean {
  return rects.some((candidate) => rectsOverlap(rect, candidate));
}

/**
 * Optional counters for callers that want to see how much work a plan took.
 * Tests use them to keep placement cost bounded on large desks.
 */
export type LayoutPlannerStats = {
  /** Candidate positions whose distance was computed. */
  candidatesScanned: number;
};

// Coordinates are integers well under 2^20, so one number keys a point.
const KEY_STRIDE = 1 << 21;

function pointKey(x: number, y: number): number {
  return x * KEY_STRIDE + y;
}

/** One axis of a candidate grid: distinct values, ascending, each with the
 * index of its first occurrence in the axis's listing order. */
type Axis = { values: number[]; firsts: number[] };

function axisFrom(listing: number[]): Axis {
  const first = new Map<number, number>();
  listing.forEach((value, index) => {
    if (!first.has(value)) first.set(value, index);
  });
  const values = Array.from(first.keys()).sort((a, b) => a - b);
  return { values, firsts: values.map((value) => first.get(value) ?? 0) };
}

/** First index in ascending `values` whose value is at least `bound`. */
function lowerIndex(values: number[], bound: number): number {
  let low = 0;
  let high = values.length;
  while (low < high) {
    const middle = (low + high) >> 1;
    if (values[middle] < bound) low = middle + 1;
    else high = middle;
  }
  return low;
}

/**
 * Every pairing of an x and a y value. `order` gives a pairing's place in the
 * grid's listing from the two first-occurrence indices; it rises with each,
 * so a point listed more than once keeps its first place.
 */
type CandidateGrid = {
  xs: Axis;
  ys: Axis;
  order: (xFirst: number, yFirst: number) => number;
  /** Blocked runs per row, kept once read, since every band reads its rows again. */
  runsByRow: ([number, number][] | undefined)[];
};

type BandCandidate = { x: number; y: number; distance: number; order: number };

/**
 * Candidate positions for a window of `size`, nearest to `preferred` first.
 * The order is that of listing, then sorting by distance with ties kept in
 * listing order and repeats dropped: the clamped preferred point; positions
 * flush against every occupied rect and the screen edges (each x with each
 * y, clamped onto the screen); then a `gridStep` lattice over the free
 * range, row by row.
 *
 * Callers stop at the first candidate that fits, so nothing is listed and
 * sorted whole. Candidates are read in distance bands that double in width
 * around `preferred`, and each band is sorted on its own. Both the edge
 * positions and the lattice are grids (clamping works per axis), so a band
 * reads only the cells inside its circle, row by row. With `blockedBy`,
 * positions where the window's own rect overlaps one of those rects are left
 * out, whole runs of a row at a time. A caller that rejects such positions
 * anyway (a footprint is never smaller than the window) gets the same first
 * fit for far less work. On a 4K desk the lattice for a small window has
 * about 14,000 points, the edge grid about 2,800, and one open asks for
 * candidates once, then once per blocker for each target it tries.
 */
export class NearestCandidates {
  private readonly preferredX: number;
  private readonly preferredY: number;
  private readonly margin: number;
  // The window's occupancy rect at a position p is p - margin, size + 2 * margin.
  private readonly occupiedWidth: number;
  private readonly occupiedHeight: number;
  private readonly blockedBy: Rect[];
  private readonly grids: CandidateGrid[];
  private readonly maxDistance: number;
  private readonly stats: LayoutPlannerStats | undefined;
  private band: BandCandidate[] = [];
  private bandIndex = 0;
  private bandKeys = new Set<number>();
  private lowerBound = Number.NEGATIVE_INFINITY;
  private upperBound: number;

  constructor(options: {
    size: Size;
    screen: Size;
    preferred: Point;
    occupiedRects: Rect[];
    margin: number;
    gridStep: number;
    blockedBy?: Rect[];
    stats?: LayoutPlannerStats;
  }) {
    const { size, screen, preferred, margin, gridStep } = options;
    this.preferredX = preferred.x;
    this.preferredY = preferred.y;
    this.margin = margin;
    this.occupiedWidth = size.width + margin * 2;
    this.occupiedHeight = size.height + margin * 2;
    this.blockedBy = options.blockedBy ?? [];
    this.stats = options.stats;

    const start = clampPositionToBounds(preferred, size, screen, margin);
    const preferredGrid: CandidateGrid = {
      xs: axisFrom([start.x]),
      ys: axisFrom([start.y]),
      order: () => 0,
      runsByRow: [],
    };

    // clampPositionToBounds works on each axis alone, so clamping every
    // pairing equals pairing the clamped values of each axis.
    const clampX = (x: number) => clampPositionToBounds({ x, y: 0 }, size, screen, margin).x;
    const clampY = (y: number) => clampPositionToBounds({ x: 0, y }, size, screen, margin).y;
    const edgeXs = new Set<number>([margin, screen.width - size.width - margin]);
    const edgeYs = new Set<number>([margin, screen.height - size.height - margin]);
    for (const rect of options.occupiedRects) {
      edgeXs.add(rect.x - size.width - margin);
      edgeXs.add(rect.x + rect.width + margin);
      edgeXs.add(rect.x);
      edgeYs.add(rect.y - size.height - margin);
      edgeYs.add(rect.y + rect.height + margin);
      edgeYs.add(rect.y);
    }
    const edgeYCount = edgeYs.size;
    const edgeGrid: CandidateGrid = {
      xs: axisFrom(Array.from(edgeXs, clampX)),
      ys: axisFrom(Array.from(edgeYs, clampY)),
      order: (xFirst, yFirst) => 1 + xFirst * edgeYCount + yFirst,
      runsByRow: [],
    };

    const latticeXs: number[] = [];
    const latticeYs: number[] = [];
    if (gridStep > 0) {
      for (let x = margin; x <= screen.width - size.width - margin; x += gridStep) latticeXs.push(Math.floor(x));
      for (let y = margin; y <= screen.height - size.height - margin; y += gridStep) latticeYs.push(Math.floor(y));
    }
    const latticeBase = 1 + edgeXs.size * edgeYCount;
    const latticeColumns = latticeXs.length;
    const latticeGrid: CandidateGrid = {
      xs: axisFrom(latticeXs),
      ys: axisFrom(latticeYs),
      order: (xFirst, yFirst) => latticeBase + yFirst * latticeColumns + xFirst,
      runsByRow: [],
    };

    this.grids = [preferredGrid, edgeGrid, latticeGrid].filter(
      (grid) => grid.xs.values.length > 0 && grid.ys.values.length > 0,
    );

    // The farthest point of a grid is one of its corners.
    let maxDistance = 0;
    for (const { xs, ys } of this.grids) {
      for (const x of [xs.values[0], xs.values[xs.values.length - 1]]) {
        for (const y of [ys.values[0], ys.values[ys.values.length - 1]]) {
          maxDistance = Math.max(maxDistance, this.distanceTo(x, y));
        }
      }
    }
    this.maxDistance = maxDistance;
    this.upperBound = Math.max(gridStep, 1) * 4;
  }

  next(): Point | null {
    for (;;) {
      while (this.bandIndex < this.band.length) {
        const candidate = this.band[this.bandIndex++];
        const key = pointKey(candidate.x, candidate.y);
        if (this.bandKeys.has(key)) continue;
        this.bandKeys.add(key);
        return { x: candidate.x, y: candidate.y };
      }
      if (this.lowerBound >= this.maxDistance) return null;
      this.fillBand();
    }
  }

  private distanceTo(x: number, y: number): number {
    return Math.hypot(x - this.preferredX, y - this.preferredY);
  }

  // The same arithmetic as rectsOverlap(occupancyRect(...), rect), split by axis.
  private overlapsInY(y: number, rect: Rect): boolean {
    const top = y - this.margin;
    return !(rect.y >= top + this.occupiedHeight || rect.y + rect.height <= top);
  }

  /**
   * Index runs of ascending `xs` where the window at row `y` overlaps a
   * blocking rect, sorted by start; runs may touch or overlap. Along x the
   * window overlaps a rect once its right edge passes the rect's left edge
   * and until its left edge reaches the rect's right edge. Both tests are
   * monotonic in x, so each run is found by two binary searches.
   */
  private blockedRuns(y: number, xs: number[]): [number, number][] {
    const runs: [number, number][] = [];
    for (const rect of this.blockedBy) {
      if (!this.overlapsInY(y, rect)) continue;
      // First x whose right edge passes the rect's left edge.
      let low = 0;
      let high = xs.length;
      while (low < high) {
        const middle = (low + high) >> 1;
        if (rect.x >= xs[middle] - this.margin + this.occupiedWidth) low = middle + 1;
        else high = middle;
      }
      const start = low;
      // First x whose left edge reaches the rect's right edge.
      high = xs.length;
      while (low < high) {
        const middle = (low + high) >> 1;
        if (rect.x + rect.width <= xs[middle] - this.margin) high = middle;
        else low = middle + 1;
      }
      if (start < low) runs.push([start, low - 1]);
    }
    return runs.sort((a, b) => a[0] - b[0]);
  }

  // Collects every candidate with lowerBound < distance <= upperBound. A
  // repeated point has the same distance as its first copy, so both always
  // land in the same band, and the band's dedupe keeps the first.
  private fillBand(): void {
    const low = this.lowerBound;
    const high = this.upperBound;
    const band: BandCandidate[] = [];
    let scanned = 0;

    for (const grid of this.grids) {
      const xs = grid.xs.values;
      const ys = grid.ys.values;
      // One pixel of slack on every bound keeps the circle safe from rounding.
      const lastRow = lowerIndex(ys, this.preferredY + high + 1);
      for (let row = lowerIndex(ys, this.preferredY - high - 1); row < lastRow; row += 1) {
        const y = ys[row];
        const dy = y - this.preferredY;
        const halfWidth = Math.sqrt(Math.max(0, high * high - dy * dy)) + 1;
        const rowLast = lowerIndex(xs, this.preferredX + halfWidth) - 1;
        let column = lowerIndex(xs, this.preferredX - halfWidth);
        const scanTo = (end: number) => {
          for (; column <= end; column += 1) {
            const x = xs[column];
            const distance = this.distanceTo(x, y);
            scanned += 1;
            if (distance > low && distance <= high) {
              band.push({ x, y, distance, order: grid.order(grid.xs.firsts[column], grid.ys.firsts[row]) });
            }
          }
        };
        if (column > rowLast) continue;
        const runs = grid.runsByRow[row] ?? (grid.runsByRow[row] = this.blockedRuns(y, xs));
        for (const [start, end] of runs) {
          scanTo(Math.min(start - 1, rowLast));
          column = Math.max(column, end + 1);
        }
        scanTo(rowLast);
      }
    }

    if (this.stats) this.stats.candidatesScanned += scanned;

    band.sort((a, b) => a.distance - b.distance || a.order - b.order);
    this.band = band;
    this.bandIndex = 0;
    this.bandKeys = new Set<number>();
    this.lowerBound = high;
    this.upperBound = high * 2;
  }
}

function findRelocationPosition(
  component: componentsMapType,
  occupiedRects: Rect[],
  screen: Size,
  margin: number,
  gridStep: number,
  footprintAt: FootprintAt,
  stats: LayoutPlannerStats | undefined,
): Point | null {
  // A footprint is never smaller than the window, so positions where the
  // window itself overlaps an occupied rect can be left out up front.
  const candidates = new NearestCandidates({
    size: component.size,
    screen,
    preferred: component.position,
    occupiedRects,
    margin,
    gridStep,
    blockedBy: occupiedRects,
    stats,
  });

  for (let candidate = candidates.next(); candidate; candidate = candidates.next()) {
    const rect = occupancyRect(candidate, footprintAt(component, candidate), margin);
    if (!rectWithinScreen(rect, screen)) continue;
    if (!overlapsAny(rect, occupiedRects)) return candidate;
  }

  return null;
}

function validateFinalLayout(
  position: Point,
  size: Size,
  movements: MovementCandidate[],
  activeComponents: componentsMapType[],
  reservedRects: Rect[],
  margin: number,
  footprintAt: FootprintAt,
): boolean {
  const movedIds = new Set(movements.map((movement) => movement.component.id));
  const finalRects = [
    ...reservedRects,
    ...activeComponents
      .filter((component) => !movedIds.has(component.id))
      .map((component) => componentRect(component, margin, footprintAt)),
  ];

  const targetRect = occupancyRect(position, size, margin);
  if (overlapsAny(targetRect, finalRects)) return false;

  finalRects.push(targetRect);

  for (const movement of movements) {
    const movedRect = occupancyRect(
      movement.toPosition,
      footprintAt(movement.component, movement.toPosition),
      margin,
    );
    if (overlapsAny(movedRect, finalRects)) return false;
    finalRects.push(movedRect);
  }

  return true;
}

function getTargetDistance(position: Point, desiredTopLeft: Point): number {
  return Math.hypot(position.x - desiredTopLeft.x, position.y - desiredTopLeft.y);
}

function getPlanCost(position: Point, desiredTopLeft: Point, movements: MovementCandidate[]): number {
  const targetCost = getTargetDistance(position, desiredTopLeft) * TARGET_DISTANCE_COST;
  const movementCountCost = movements.length * MOVEMENT_COUNT_COST;
  const movementTotalCost = movements.reduce((sum, movement) => sum + movement.cost, 0);
  return targetCost + movementCountCost + movementTotalCost;
}

export function planComponentPlacement(options: LayoutPlannerOptions): PlacementStrategy {
  const margin = options.margin ?? DEFAULT_MARGIN;
  const gridStep = options.gridStep ?? DEFAULT_GRID_STEP;
  const maxMovements = options.maxMovements ?? DEFAULT_MAX_MOVEMENTS;
  const maxTargetCandidates = options.maxTargetCandidates ?? DEFAULT_MAX_TARGET_CANDIDATES;
  const fixedNames = options.fixedComponentNames ?? new Set<string>();
  const footprintAt = options.footprintAt ?? ownSize;
  const reservedRects = options.reservedRects ?? [];

  const screen = options.screen;
  const newSize = options.newComponentSize;
  const desiredTopLeft = clampPositionToBounds(
    {
      x: options.desiredCenter.x - newSize.width / 2,
      y: options.desiredCenter.y - newSize.height / 2,
    },
    newSize,
    screen,
    margin,
  );

  const activeComponents = options.components.filter((component) => !isHiddenComponent(component));
  // Reserved rects join the occupied rects so candidates also sit flush
  // against them (a window right below the desktop rail, for one).
  const activeRects = [
    ...reservedRects,
    ...activeComponents.map((component) => componentRect(component, margin, footprintAt)),
  ];
  const fixedRects = [
    ...reservedRects,
    ...activeComponents
      .filter((component) => isFixedComponent(component, fixedNames))
      .map((component) => componentRect(component, margin, footprintAt)),
  ];
  const movableComponents = activeComponents.filter((component) => !isFixedComponent(component, fixedNames));
  const targetCandidates = new NearestCandidates({
    size: newSize,
    screen,
    preferred: desiredTopLeft,
    occupiedRects: activeRects,
    margin,
    gridStep,
    stats: options.stats,
  });

  let bestPlan: (PlacementStrategy & { planCost: number }) | null = null;

  for (let tried = 0; tried < maxTargetCandidates; tried += 1) {
    const targetPosition = targetCandidates.next();
    if (!targetPosition) break;

    // Candidates are sorted by distance from the desired point, and the
    // distance term alone is a lower bound on any plan's cost — once it
    // exceeds the best plan found, no remaining candidate can win.
    if (bestPlan && getTargetDistance(targetPosition, desiredTopLeft) * TARGET_DISTANCE_COST >= bestPlan.planCost) {
      break;
    }

    const targetRect = occupancyRect(targetPosition, newSize, margin);
    if (!rectWithinScreen(targetRect, screen)) continue;
    if (overlapsAny(targetRect, fixedRects)) continue;

    const blockers = movableComponents.filter((component) => rectsOverlap(componentRect(component, margin, footprintAt), targetRect));
    if (blockers.length > maxMovements) continue;

    if (blockers.length === 0) {
      const placementStrategy = {
        needsMovement: false,
        position: targetPosition,
        componentsToMove: [],
        targetBoundingBox: { x: targetPosition.x, y: targetPosition.y, width: newSize.width, height: newSize.height },
        allBlockersResolved: true,
      };
      const planCost = getPlanCost(targetPosition, desiredTopLeft, []);
      if (!bestPlan || planCost < bestPlan.planCost) {
        bestPlan = { ...placementStrategy, planCost };
      }
      continue;
    }

    const blockerIds = new Set(blockers.map((component) => component.id));
    const occupiedRects = [
      ...reservedRects,
      ...activeComponents
        .filter((component) => !blockerIds.has(component.id))
        .map((component) => componentRect(component, margin, footprintAt)),
    ];

    occupiedRects.push(targetRect);

    const orderedBlockers = [...blockers].sort((a, b) => {
      const areaDiff = b.size.width * b.size.height - a.size.width * a.size.height;
      if (areaDiff !== 0) return areaDiff;
      return (b.score || 5) - (a.score || 5);
    });

    const movements: MovementCandidate[] = [];
    let failed = false;

    for (const blocker of orderedBlockers) {
      const relocation = findRelocationPosition(
        blocker,
        occupiedRects,
        screen,
        margin,
        gridStep,
        footprintAt,
        options.stats,
      );
      if (!relocation) {
        failed = true;
        break;
      }

      const candidate: MovementCandidate = {
        component: blocker,
        fromPosition: { ...blocker.position },
        toPosition: relocation,
        distance: Math.hypot(relocation.x - blocker.position.x, relocation.y - blocker.position.y),
        cost: movementCost(blocker, relocation),
        relatedComponents: [],
      };

      movements.push(candidate);
      occupiedRects.push(occupancyRect(relocation, footprintAt(blocker, relocation), margin));
    }

    if (failed || movements.length !== blockers.length) continue;
    if (!validateFinalLayout(targetPosition, newSize, movements, activeComponents, reservedRects, margin, footprintAt)) continue;

    const planCost = getPlanCost(targetPosition, desiredTopLeft, movements);
    const placementStrategy = {
      needsMovement: true,
      position: null,
      componentsToMove: movements,
      targetBoundingBox: { x: targetPosition.x, y: targetPosition.y, width: newSize.width, height: newSize.height },
      allBlockersResolved: true,
    };

    if (!bestPlan || planCost < bestPlan.planCost) {
      bestPlan = {
        ...placementStrategy,
        planCost,
      };
    }
  }

  if (bestPlan) {
    const { planCost: _planCost, ...placementStrategy } = bestPlan;
    return placementStrategy;
  }

  return {
    needsMovement: false,
    position: null,
    componentsToMove: [],
    allBlockersResolved: false,
  };
}
